import { useLocalization } from "@fluent/react";
import { Button, ResponsiveFlex } from "@narmi/design_system";

import AccountFilterPopover from "../AccountFilterPopover/AccountFilterPopover";
import DateRangeFilterPopover from "../DateRangeFilterPopover/DateRangeFilterPopover";
import SavedViewsDropdown from "../SavedViewsDropdown/SavedViewsDropdown";
import TransactionTypeFilter from "../TransactionTypeFilter/TransactionTypeFilter";

import styles from "./FilterBar.module.scss";

import type Account from "byzantine/src/Account";
import type { DatePreset } from "../../constants";
import type { DateRangeSelection } from "../DateRangeFilterPopover/DateRangeFilterPopover";
import type { BaiTransactionType } from "../../hooks/useBaiTransactionTypes";
import type { AdvancedExportSavedView } from "../../hooks/useSavedViews";

interface FilterBarProps {
  accounts: Account[];
  selectedAccountIds: string[];
  onAccountSelectionChange: (ids: string[]) => void;
  startDate: string;
  endDate: string;
  selectedPreset: DatePreset | null;
  onDateRangeChange: (selection: DateRangeSelection | null) => void;
  selectedTypeCodes: string[];
  onTypeCodesChange: (codes: string[]) => void;
  transactionTypes: BaiTransactionType[];
  transactionTypesLoading: boolean;
  transactionTypesError: string | null;
  hasActiveFilters: boolean;
  onClearFilters: () => void;
  savedViews: AdvancedExportSavedView[];
  savedViewsLoading: boolean;
  selectedView: AdvancedExportSavedView | null;
  onSelectView: (viewId: string) => void;
  isModified: boolean;
  isPresetView: boolean;
  onSaveNewView: () => void;
  onUpdateView: () => void;
}

const FilterBar = ({
  accounts,
  selectedAccountIds,
  onAccountSelectionChange,
  startDate,
  endDate,
  selectedPreset,
  onDateRangeChange,
  selectedTypeCodes,
  onTypeCodesChange,
  transactionTypes,
  transactionTypesLoading,
  transactionTypesError,
  hasActiveFilters,
  onClearFilters,
  savedViews,
  savedViewsLoading,
  selectedView,
  onSelectView,
  isModified,
  isPresetView,
  onSaveNewView,
  onUpdateView,
}: FilterBarProps) => {
  const { l10n } = useLocalization();

  return (
    <div className={styles["filter-bar"]}>
      <ResponsiveFlex direction="column" toRowAt="l" gapSize="m">
        <div className={styles["filter-chips"]}>
          <SavedViewsDropdown
            views={savedViews}
            isLoading={savedViewsLoading}
            selectedView={selectedView}
            onSelectView={onSelectView}
          />
          <AccountFilterPopover
            accounts={accounts}
            selectedAccountIds={selectedAccountIds}
            onSelectionChange={onAccountSelectionChange}
          />
          <DateRangeFilterPopover
            startDate={startDate}
            endDate={endDate}
            selectedPreset={selectedPreset}
            onChange={onDateRangeChange}
          />
          <TransactionTypeFilter
            selectedCodes={selectedTypeCodes}
            onChange={onTypeCodesChange}
            types={transactionTypes}
            isLoading={transactionTypesLoading}
            error={transactionTypesError}
          />
        </div>

        {hasActiveFilters && (
          <div className={styles["action-buttons"]}>
            <Button
              kind="tonal"
              size="xs"
              label={l10n.getString("transaction-export-clear-filter")}
              onClick={onClearFilters}
              type="button"
            />
            {isModified && !isPresetView && selectedView && (
              <Button
                kind="primary"
                size="xs"
                label={l10n.getString("transaction-export-update-view")}
                onClick={onUpdateView}
                type="button"
              />
            )}
            {(!selectedView || isModified) &&
              !(isModified && !isPresetView && selectedView) && (
                <Button
                  kind="primary"
                  size="xs"
                  label={l10n.getString("transaction-export-save-new-view")}
                  onClick={onSaveNewView}
                  type="button"
                />
              )}
          </div>
        )}
      </ResponsiveFlex>
    </div>
  );
};

export default FilterBar;
